import { Link, useLocation } from 'react-router-dom'

import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import { cn } from '@/lib/utils'

export type WorkerLifecycleStage =
  | 'workers'
  | 'worker-skills'
  | 'pay-scale'
  | 'assignments'
  | 'workday-entries'

const buildScopedLifecyclePaths = (
  basePath?: string,
): { workers: string; assignments: string; workdays: string } => {
  if (basePath === '/events') {
    return {
      workers: '/event/crew',
      assignments: '/events/assignments',
      workdays: '/events/workdays',
    }
  }

  if (basePath === '/projects') {
    return {
      workers: '/workers',
      assignments: '/projects/assignments',
      workdays: '/projects/workdays',
    }
  }

  if (basePath === '/job') {
    return {
      workers: '/job/workers',
      assignments: '/job/assignments',
      workdays: '/workdays',
    }
  }

  return {
    workers: '/workers',
    assignments: '/assignments',
    workdays: '/workdays',
  }
}

const inferBasePathFromPathname = (pathname: string): '/job' | '/projects' | '/events' | undefined => {
  if (pathname.startsWith('/event/') || pathname === '/event/crew') {
    return '/events'
  }
  if (pathname.startsWith('/events/')) {
    return '/events'
  }
  if (pathname.startsWith('/projects/')) {
    return '/projects'
  }
  if (pathname.startsWith('/job/')) {
    return '/job'
  }
  return undefined
}

const buildWorkerLifecycleSteps = (
  basePath?: string,
  workdayEntriesLabel = 'WorkDay Entries',
): Array<{
  key: WorkerLifecycleStage
  label: string
  path: string
  tone: string
}> => {
  const scopedPaths = buildScopedLifecyclePaths(basePath)
  return [
    { key: 'workers', label: '1. Workers', path: scopedPaths.workers, tone: 'border-emerald-200 bg-emerald-50 text-emerald-700' },
    { key: 'worker-skills', label: '2. Worker Skills', path: '/services/worker-skills', tone: 'border-sky-200 bg-sky-50 text-sky-700' },
    { key: 'pay-scale', label: '3. PayScale', path: '/services/pay-scales', tone: 'border-amber-200 bg-amber-50 text-amber-700' },
    { key: 'assignments', label: '4. Assignments', path: scopedPaths.assignments, tone: 'border-violet-200 bg-violet-50 text-violet-700' },
    { key: 'workday-entries', label: `5. ${workdayEntriesLabel}`, path: scopedPaths.workdays, tone: 'border-rose-200 bg-rose-50 text-rose-700' },
  ]
}

export function resolveWorkerLifecycleStage(pathname: string): WorkerLifecycleStage | null {
  if (pathname.startsWith('/services/worker-skills')) {
    return 'worker-skills'
  }

  if (pathname.startsWith('/services/pay-scales')) {
    return 'pay-scale'
  }

  if (
    pathname.startsWith('/assignments')
    || pathname.startsWith('/job/assignments')
    || pathname.startsWith('/events/assignments')
    || pathname.startsWith('/projects/assignments')
  ) {
    return 'assignments'
  }

  if (
    pathname.startsWith('/workdays')
    || pathname.startsWith('/events/workdays')
    || pathname.startsWith('/projects/workdays')
  ) {
    return 'workday-entries'
  }

  if (pathname.startsWith('/workers')) {
    return 'workers'
  }

  if (pathname.startsWith('/job/workers')) {
    return 'workers'
  }

  if (pathname.startsWith('/event/crew') || pathname.startsWith('/events/crew')) {
    return 'workers'
  }

  return null
}

interface WorkerLifecycleNavProps {
  activeStage?: WorkerLifecycleStage
  description?: string
  showDescription?: boolean
  className?: string
  basePath?: '/job' | '/projects' | '/events'
  workdayEntriesLabel?: string
}

export function WorkerLifecycleNav({
  activeStage,
  description = 'Move from worker onboarding to skills, pay rates, assignments, and daily work entries.',
  showDescription = false,
  className,
  basePath,
  workdayEntriesLabel,
}: WorkerLifecycleNavProps) {
  const location = useLocation()
  const currentStage = activeStage ?? resolveWorkerLifecycleStage(location.pathname)
  const effectiveBasePath = basePath ?? inferBasePathFromPathname(location.pathname)
  const steps = buildWorkerLifecycleSteps(effectiveBasePath, workdayEntriesLabel)
  const normalizedSearch = location.search ? (location.search.startsWith('?') ? location.search : `?${location.search}`) : ''

  if (!currentStage) {
    return null
  }

  return (
    <Card
      className={cn(
        'rounded-xl border-white/70 bg-[linear-gradient(135deg,rgba(255,255,255,0.96),rgba(248,250,252,0.92))] shadow-[0_12px_24px_-24px_rgba(15,23,42,0.45)]',
        className,
      )}
    >
      <CardContent className="flex flex-col gap-1.5 px-3 py-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0 shrink-0">
          <p className="text-xs font-medium text-foreground">Worker lifecycle workflow</p>
          {showDescription ? (
            <p className="mt-0.5 text-[11px] leading-snug text-muted-foreground">{description}</p>
          ) : null}
        </div>
        <nav
          aria-label="Worker lifecycle"
          className="flex flex-wrap items-center gap-1 text-[11px] md:text-xs"
        >
          {steps.map((step, index) => {
            const isActive = currentStage === step.key
            const href = `${step.path}${normalizedSearch}`
            const badge = (
              <Badge
                variant="outline"
                className={cn(
                  'px-2 py-0.5 text-[11px] font-normal',
                  step.tone,
                  isActive ? 'ring-1 ring-offset-1 ring-offset-background shadow-sm' : undefined,
                )}
              >
                {step.label}
              </Badge>
            )

            return (
              <div key={step.key} className="flex items-center gap-1">
                <Link to={href} className="rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                  {badge}
                </Link>
                {index < steps.length - 1 ? (
                  <span className="text-[10px] text-muted-foreground" aria-hidden>&rarr;</span>
                ) : null}
              </div>
            )
          })}
        </nav>
      </CardContent>
    </Card>
  )
}
